// src/pages/ConfirmEmailChangePage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import api from '../api/axiosConfig';

type ConfirmState = 'idle' | 'loading' | 'success' | 'error';

const PANEL = 'rgba(10,16,28,0.82)';
const BORDER = '1px solid rgba(148,163,184,0.16)';
const TXT = '#eaf2ff';
const SUB = 'rgba(234,242,255,.72)';
const BLUE = '#60a5fa';

const ConfirmEmailChangePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => (searchParams.get('token') ?? '').trim(), [searchParams]);

  const [status, setStatus] = useState<ConfirmState>('idle');
  const [message, setMessage] = useState('이메일 변경을 확인하는 중입니다.');
  const [detail, setDetail] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('유효하지 않은 요청입니다.');
        setDetail('이메일 링크에 포함된 토큰이 없습니다. 다시 이메일 변경을 요청해 주세요.');
        return;
      }

      try {
        setStatus('loading');
        setMessage('이메일 변경을 확인하는 중입니다.');
        setDetail('');

        const { data } = await api.get('/user/email/change-confirm', {
          params: { token },
        });

        if (cancelled) return;

        setStatus('success');
        setMessage(data?.message || '이메일 변경이 완료되었습니다.');
        setDetail('보안을 위해 다시 로그인해 주는 것이 안전합니다.');
      } catch (error: any) {
        if (cancelled) return;

        const serverMessage =
          error?.response?.data?.message ||
          (typeof error?.response?.data === 'string' ? error.response.data : '') ||
          '이메일 변경 확인에 실패했습니다.';

        setStatus('error');
        setMessage('이메일 변경을 완료하지 못했습니다.');
        setDetail(serverMessage);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(900px 500px at 0% 0%, rgba(96,165,250,.10), transparent 35%), radial-gradient(900px 500px at 100% 0%, rgba(251,191,36,.08), transparent 35%), #0b1220',
        display: 'flex',
        alignItems: 'center',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            background: PANEL,
            border: BORDER,
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                background:
                  status === 'success'
                    ? 'rgba(52,211,153,.12)'
                    : status === 'error'
                      ? 'rgba(244,63,94,.12)'
                      : 'rgba(96,165,250,.12)',
                border:
                  status === 'success'
                    ? '1px solid rgba(52,211,153,.22)'
                    : status === 'error'
                      ? '1px solid rgba(244,63,94,.22)'
                      : '1px solid rgba(96,165,250,.22)',
                color:
                  status === 'success'
                    ? '#86efac'
                    : status === 'error'
                      ? '#fda4af'
                      : '#93c5fd',
              }}
            >
              {status === 'success' ? (
                <MarkEmailReadRoundedIcon sx={{ fontSize: 30 }} />
              ) : status === 'error' ? (
                <ErrorOutlineRoundedIcon sx={{ fontSize: 30 }} />
              ) : (
                <CircularProgress size={28} sx={{ color: '#93c5fd' }} />
              )}
            </Box>

            <Box>
              <Typography sx={{ color: TXT, fontSize: 24, fontWeight: 900 }}>
                이메일 변경 확인
              </Typography>
              <Typography sx={{ color: SUB, fontSize: 14, mt: 1, lineHeight: 1.7 }}>
                {message}
              </Typography>
            </Box>

            {status === 'success' && (
              <Alert severity="success" sx={{ width: '100%', borderRadius: 2, textAlign: 'left' }}>
                {detail}
              </Alert>
            )}

            {status === 'error' && (
              <Alert severity="error" sx={{ width: '100%', borderRadius: 2, textAlign: 'left' }}>
                {detail || '잠시 후 다시 시도해 주세요.'}
              </Alert>
            )}

            {status === 'loading' && (
              <Alert severity="info" sx={{ width: '100%', borderRadius: 2, textAlign: 'left' }}>
                링크 유효성을 검사하고 이메일 변경을 처리하고 있습니다.
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.2}
              sx={{ width: '100%', pt: 0.5 }}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HomeRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{
                  minHeight: 44,
                  borderRadius: 2,
                  color: TXT,
                  borderColor: 'rgba(148,163,184,.22)',
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                메인으로 이동
              </Button>

              <Button
                fullWidth
                variant="contained"
                startIcon={<LoginRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{
                  minHeight: 44,
                  borderRadius: 2,
                  bgcolor: BLUE,
                  color: '#061225',
                  fontWeight: 900,
                  textTransform: 'none',
                  '&:hover': { bgcolor: BLUE, filter: 'brightness(1.04)' },
                }}
              >
                로그인하러 가기
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ConfirmEmailChangePage;